import { useMemo } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { CheckList, CheckListItemType } from "../../../../../components/check-list/CheckList";
import { ContentContainer } from "../../../../../components/content-container/ContentContainer";
import { ContentSection } from "../../../../../components/content-section/ContentSection";
import SideSheetFilterInput from "../../../../../components/side-sheet/side-sheet-filter-input/SideSheetFilterInput";
import SideSheetSwitch from "../../../../../components/side-sheet/side-sheet-switch/SideSheetSwitch";
import SideSheet from "../../../../../components/side-sheet/side-sheet/SideSheet";
import { StepMenuStepProps } from "../../../../../components/step-menu/StepMenu";
import TabBar from "../../../../../components/tab-bar/TabBar";
import TabWithCount from "../../../../../components/tab-with-count/TabWithCount";
import { useRouteContext } from "../../../../../hooks/use-route-context/UseRouteContext";
import { useQueryNotifications } from "../../../../../queries/UseQueryNotifications";
import { RoleSortEnum, useRoles } from "../../../../../queries/user-role-administration/UseRoles";
import { createAvailableRoleCheckListItems } from "../UserEditorUtils";
import { UserAssociatedRoleFieldType, UserFieldType } from "../UserFieldTypes";
import { useMembershipStepState } from "./MembershipStepState";

export type MembershipStepProps = {
    organizationID: number | undefined;
} & StepMenuStepProps;

export default function MembershipStep({
    organizationID
}: MembershipStepProps) {

    const routeContext = useRouteContext();

    const { t } = useTranslation();

    const {
        state: {
            filterValue,
            viewSelected
        },
        setFilterValue,
        setViewSelected
    } = useMembershipStepState();

    const {
        data: roleData,
    } = useQueryNotifications(useRoles)({
        groupID: routeContext.groupID as number,
        organizationID: organizationID as number,
        options: { filterText: filterValue, sortBy: RoleSortEnum.NameAsc },
        enabled: organizationID !== undefined
    });

    const {
        setValue
    } = useFormContext<UserFieldType>();

    const {
        fields: selectedRoles,
        append: appendSelectedRole,
        remove: removeSelectedRole
    } = useFieldArray<UserFieldType, "associatedRoles", "fieldKey">({ name: "associatedRoles", keyName: "fieldKey" });

    const availableRoles = useMemo(() => {

        return createAvailableRoleCheckListItems(
            roleData?.data,
            selectedRoles
        );
    }, [
        roleData?.data,
        selectedRoles
    ]);

    function onRoleClicked(item: CheckListItemType) {
        updateRoles(item);
    }

    function updateRoles(item: CheckListItemType) {
        if (item.checked) {

            const role: UserAssociatedRoleFieldType = { id: Number(item.key) };

            appendSelectedRole(role, { shouldFocus: false });

            if(item.displayText.toLowerCase() === "elynx admin") {
                setValue("isElynxAdmin", true);
            }
        } else {

            // remove from array
            const roleIndex = selectedRoles.findIndex(c => `${c.id}` === item.key);
            if (roleIndex > -1) {
                removeSelectedRole(roleIndex);
            }

            if(item.displayText.toLowerCase() === "elynx admin") {
                setValue("isElynxAdmin", false);
            }
        }
    }

    const roleCount = availableRoles.filter(r => r.checked).length;

    return (
        <>
            <TabBar value={0} >
                <TabWithCount label="Roles"
                    count={roleCount}
                    value={0}
                />
            </TabBar>

            <ContentContainer>
                <ContentSection>
                    <CheckList
                        items={availableRoles}
                        onItemClick={onRoleClicked}
                        filter={
                            (item: CheckListItemType) => {
                                return !viewSelected || item.checked;
                            }
                        }
                    />
                </ContentSection>

                <SideSheet
                    title={t("options")}
                    open={true}
                >
                    <SideSheetFilterInput
                        label={t("userEditor.steps.membership.filter")}
                        value={filterValue}
                        onChange={(value: string) => {
                            setFilterValue(value);
                        }}
                    />

                    <SideSheetSwitch
                        label={t("userEditor.steps.membership.viewSelected")}
                        checked={viewSelected}
                        onChange={(checked: boolean) => {
                            setViewSelected(checked);
                        }}
                    />
                </SideSheet>
            </ContentContainer>
        </>
    );
}



